import React, { useState } from 'react'

import { CheckOutlined } from '@ant-design/icons'
import { Popover, Typography } from 'antd'
import clsx from 'clsx'

export interface SortOption {
  text: string
  field: string
  order: 'ascend' | 'descend'
}

export interface SortSelectProps {
  sortOptions: SortOption[]
  defSortOption: Omit<SortOption, 'text'> | undefined
  onChange?: (opt: SortOption | undefined) => void
}

const SortSelect: React.FC<React.PropsWithChildren<SortSelectProps>> = ({
  sortOptions,
  defSortOption,
  onChange,
  children
}) => {
  const [curOption, setCurOption] = useState<SortOption | undefined>(() => {
    return sortOptions.find(
      (o) =>
        o.field === defSortOption?.field && o.order === defSortOption?.order
    )
  })

  function handleClick(opt: SortOption) {
    if (opt === curOption) {
      // toggle
      setCurOption(undefined)
      onChange?.(undefined)
    } else {
      // switch
      setCurOption(opt)
      onChange?.(opt)
    }
  }

  const content = (
    <div className='w-max ml-[-16px] mr-[-16px] mt-[-12px] mb-[-12px]'>
      {sortOptions.map((o, idx) => (
        <div
          className={clsx(
            'flex items-center border-0  border-gray-200 border-solid cursor-pointer pl-4 pr-4 pt-2 pb-2 hover:bg-gray-100',
            idx !== 0 && 'border-t'
          )}
          key={o.text}
          onClick={() => handleClick(o)}
        >
          <Typography.Text className='flex-1 pr-4'>{o.text}</Typography.Text>
          <CheckOutlined
            className='text-cc-primary font-bold ml-auto'
            style={{ opacity: o === curOption ? 1 : 0 }}
          />
        </div>
      ))}
    </div>
  )

  return (
    <Popover title='Sort By' content={content}>
      {children}
    </Popover>
  )
}

const overviewSortOptions: SortOption[] = [
  {
    text: 'Recently created',
    field: 'clusterCreatedAt',
    order: 'descend'
  },
  {
    text: 'Least recently created',
    field: 'clusterCreatedAt',
    order: 'ascend'
  },
  {
    text: 'Recently deleted',
    field: 'clusterDeletedAt',
    order: 'descend'
  },
  {
    text: 'Least recently deleted',
    field: 'clusterDeletedAt',
    order: 'ascend'
  },
  {
    text: 'Fastest created',
    field: 'clusterDuration',
    order: 'ascend'
  },
  {
    text: 'Slowest created',
    field: 'clusterDuration',
    order: 'descend'
  }
]

const OverviewSortSelect: React.FC<
  React.PropsWithChildren<Omit<SortSelectProps, 'sortOptions'>>
> = (props) => {
  return <SortSelect sortOptions={overviewSortOptions} {...props} />
}

export { SortSelect, OverviewSortSelect }
